import clsx from 'clsx';
import React from 'react';
import { DefaultToast } from 'react-toast-notifications';
import Icon from '../../atoms/Icon/Icon';
import type { ToastProps } from './types';
import useToast from './useToast';

/**
 * Toast — v2 molecule.
 *
 * Wrapper presentacional sobre `DefaultToast` de `react-toast-notifications`.
 * Se registra una sola vez en el `<ToastProvider>` (`src/App.js`) y se dispara
 * desde cualquier punto con `useToasts().addToast(mensaje, { appearance })`.
 *
 * Accesibilidad:
 *  - `aria-live="polite"` para anunciar el mensaje a lectores de pantalla.
 *  - Botón de cierre propio, visible en todas las variantes y con `aria-label`
 *    (el botón nativo de la librería no expone nombre accesible y se oculta vía CSS).
 *  - El timeout (≥ 5s WCAG) se configura con `autoDismissTimeout` en el `ToastProvider`.
 */
function Toast({ appearance, children, onDismiss, ...props }: Readonly<ToastProps>): JSX.Element {
  const { dismissAriaLabel, iconName } = useToast({ appearance });

  return (
    <DefaultToast appearance={appearance} onDismiss={onDismiss} {...props}>
      <div aria-live="polite" className={clsx('toast', appearance && `toast--${appearance}`)}>
        {iconName && <Icon color="inherit" name={iconName} size="lg" />}
        <span className="toast__content">{children}</span>
        <button aria-label={dismissAriaLabel} className="toast__dismiss" onClick={onDismiss} type="button">
          <Icon color="inherit" name="close" size="sm" />
        </button>
      </div>
    </DefaultToast>
  );
}

export default Toast;
